import React from 'react';
import { array, bool, func, string } from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { Modal, AddToCalendar } from '../../components';

import css from './TourSchedulesModal.module.css';

const TourSchedulesModal = props => {
  const {
    id,
    containerClassName,
    contentClassName,
    isOpen,
    onCloseModal,
    onManageDisableScrolling,
    tourSchedules,
    listingTitle,
    intl,
  } = props;

  const formatDate = timestamp => {
    if (!timestamp) return 'N/A';

    const date = new Date(timestamp);
    const currentYear = new Date().getFullYear();
    const dateYear = date.getFullYear();

    const options = {
      month: 'short',
      day: 'numeric',
    };

    let formattedDate = date.toLocaleDateString(intl.locale, options);

    // Add year if it's not the current year
    if (dateYear !== currentYear) {
      formattedDate += ` ${dateYear}`;
    }

    return formattedDate;
  };

  const formatLocation = location => {
    if (!location || !location.address) {
      return intl.formatMessage({ id: 'TourSchedulesModal.locationNotAvailable' });
    }
    return location.address;
  };

  const modalTitle = intl.formatMessage({ id: 'TourSchedulesModal.title' }, { listingTitle });

  // Sort schedules by closest start date
  const sortedSchedules = [...tourSchedules].sort((a, b) => {
    const dateA = a.startDate ? new Date(a.startDate) : new Date(9999, 11, 31);
    const dateB = b.startDate ? new Date(b.startDate) : new Date(9999, 11, 31);
    return dateA - dateB;
  });

  const schedulesList = sortedSchedules.map((schedule, index) => {
    const startDate = formatDate(schedule.startDate);
    const endDate = formatDate(schedule.endDate);
    const location = formatLocation(schedule.location);
    const dateRange = `${startDate} - ${endDate}`;

    // Format dates for calendar
    const formatCalendarDate = timestamp => {
      if (!timestamp) return null;
      const date = new Date(timestamp);
      // Format: YYYY-MM-DD
      return date.toISOString().split('T')[0];
    };

    const calendarStartDate = formatCalendarDate(schedule.startDate);
    const calendarEndDate = formatCalendarDate(schedule.endDate);

    // Prepare booking data for AddToCalendar
    const bookingData =
      calendarStartDate && calendarEndDate
        ? {
            title: listingTitle,
            description: intl.formatMessage(
              { id: 'TourSchedulesModal.calendarDescription' },
              { listingTitle, location }
            ),
            location: location,
            start: calendarStartDate,
            end: calendarEndDate,
          }
        : null;

    return (
      <div key={index} className={css.scheduleItem}>
        <div className={css.scheduleDateRange}>{dateRange}</div>
        <div className={css.scheduleInfo}>
          <div className={css.scheduleDetails}>
            <div className={css.scheduleTitle}>{listingTitle}</div>
            <div className={css.scheduleLocation}>{location}</div>
          </div>
          {bookingData && (
            <div className={css.calendarButtonWrapper}>
              <AddToCalendar
                className={css.calendarButton}
                bookingLabel={intl.formatMessage({ id: 'TourSchedulesModal.addToCalendar' })}
                bookingData={bookingData}
              />
            </div>
          )}
        </div>
      </div>
    );
  });

  return (
    <Modal
      id={id}
      containerClassName={containerClassName}
      contentClassName={contentClassName}
      isOpen={isOpen}
      onClose={onCloseModal}
      onManageDisableScrolling={onManageDisableScrolling}
    >
      <div className={css.modalContent}>
        <h2 className={css.modalTitle}>{modalTitle}</h2>
        <div className={css.schedulesContainer}>
          {schedulesList.length > 0 ? (
            schedulesList
          ) : (
            <p className={css.noSchedulesMessage}>
              <FormattedMessage id="TourSchedulesModal.noSchedules" />
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

TourSchedulesModal.propTypes = {
  id: string.isRequired,
  containerClassName: string,
  contentClassName: string,
  isOpen: bool.isRequired,
  onCloseModal: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  tourSchedules: array,
  listingTitle: string,
  intl: intlShape.isRequired,
};

TourSchedulesModal.defaultProps = {
  tourSchedules: [],
  listingTitle: '',
};

export default injectIntl(TourSchedulesModal);
